import { BidListResponse, TBidDto } from "interfaces";
import { components, operations } from "../types/openapi";
import { api } from "./api";
import { TRequestValues } from "./authClient/auth/api-utils";
import { ENDPOINTS } from "./endpoints";

type getAllContractsOperation = operations["getAll_1"];
type getContractByIdOperation = operations["getById_2"];
type updateContractOperation = operations["update"];
type createContractOperation = operations["create_3"];
type approveContractOperation = operations["approve_1"];
type declineContractDraftOperation = operations["declineDraft_1"];
type blockContractOperation = operations["block"];
type unblockContractOperation = operations["unblock"];
type attachContractDocumentOperation = operations["attachContractDocument"];
type getContractAvailableLimitOperation = operations["availableContractLimit"];
type getAllByCompanyIdOperation = operations["getAllByCompanyId"];
type getOksmOperation = operations["getOksm"];
type getAllCurrencyCodeOperation = operations["getAllCurrencyCode"];
type returnContractToDraftOperation = operations["draft"];
type validateUnkOperation = operations["validateUnk"];
type generateRequestsByContractReport = operations["generateRequestsByContractReport"];
type returnContractToDraftForCustomerOperation = operations["returnRequestToDraft"];
type getContractHistoryOperation = operations["getHistory_1"];

export type ContractHistoryRow = components["schemas"]["ContractHistoryDto"];

/**
 * Грид договоров (доступно для CUSTOMER, OPERATOR)
 */
export const getContractList = (params: Record<string, TRequestValues>): Promise<BidListResponse> => {
  return api.get(ENDPOINTS.contracts(), params);
};

/**
 * Карточка договора (доступно для CUSTOMER, OPERATOR)
 */
export const getContractById = ({
  id,
}: getContractByIdOperation["parameters"]["path"]): Promise<
  getContractByIdOperation["responses"]["200"]["content"]["*/*"]
> => {
  return api.get(ENDPOINTS.contractById(id));
};

/**
 * История статусов договора (GET /contract/{id}/history)
 */
export const getContractStatusHistory = ({
  id,
}: getContractHistoryOperation["parameters"]["path"]): Promise<ContractHistoryRow[]> => {
  return api.get(ENDPOINTS.contractHistory(id));
};

/**
 * Создать новый договор (доступно для CUSTOMER, OPERATOR)
 */
export const createContract = (
  formData: createContractOperation["requestBody"]["content"]["application/json"]
): Promise<string> => {
  return api.post(ENDPOINTS.contracts(), formData, {
    contentType: "multipart/form-data",
  });
};

/**
 * Обновление данных договора (доступно для OPERATOR)
 */
export const editContract = (
  { id }: updateContractOperation["parameters"]["path"],
  formData: updateContractOperation["requestBody"]["content"]["application/json"]
): Promise<updateContractOperation["responses"]["200"]["content"]> => {
  return api.put(ENDPOINTS.updateContractById(id), formData, {
    contentType: "multipart/form-data",
  });
};

/**
 * Утвердить договор (доступно для OPERATOR)
 */
export const approveContract = ({
  id,
}: approveContractOperation["parameters"]["path"]): Promise<
  approveContractOperation["responses"]["200"]["content"]
> => {
  return api.post(ENDPOINTS.approveContractById(id));
};

/**
 * Отклонить черновик договора (доступно для OPERATOR)
 */
export const declineContractDraft = (
  { id }: declineContractDraftOperation["parameters"]["path"],
  formData: declineContractDraftOperation["requestBody"]["content"]["application/json"]
): Promise<declineContractDraftOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.declineContractDraft(id), formData);
};

/**
 * Заблокировать договор (доступно для OPERATOR)
 */
export const blockContract = (
  { id }: blockContractOperation["parameters"]["path"],
  formData: blockContractOperation["requestBody"]["content"]["application/json"]
): Promise<blockContractOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.blockContract(id), formData);
};

/**
 * Разблокировать договор (доступно для OPERATOR)
 */
export const unblockContract = ({
  id,
}: unblockContractOperation["parameters"]["path"]): Promise<unblockContractOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.unblockContract(id));
};

/**
 * Прикрепить документ к договору (доступно для CUSTOMER, OPERATOR)
 */
export const attachContractDocument = (
  { contractId }: attachContractDocumentOperation["parameters"]["path"],
  formData: attachContractDocumentOperation["requestBody"]["content"]["application/json"]
): Promise<attachContractDocumentOperation["responses"]["200"]["content"]> => {
  return api.put(ENDPOINTS.uploadContractFile(contractId), formData, {
    contentType: "multipart/form-data",
  });
};

/**
 * Получить доступный лимит по договору
 */
export const getContractAvailableLimit = ({
  id,
}: getContractAvailableLimitOperation["parameters"]["path"]): Promise<
  getContractAvailableLimitOperation["responses"]["200"]["content"]["*/*"]
> => {
  return api.get(ENDPOINTS.contractAvailableLimit(id));
};

/**
 * Грид договоров для компании (OPERATOR)
 */
export const getCompanyContractList = (
  { companyId }: getAllByCompanyIdOperation["parameters"]["path"],
  params: Record<string, TRequestValues>
): Promise<getAllByCompanyIdOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.companyContracts(companyId), params);
};

/**
 * Возвращает с пагинацией список стран с их кодами согласно ОКСМ
 */
export const getCountryList = (
  params: getOksmOperation["parameters"]["query"]
): Promise<getOksmOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.countryList(), params);
};

/**
 * Получение списка поддерживаемых системой валют с возможностью поиска (доступно для CUSTOMER, OPERATOR, LIQUIDITY_PROVIDER)
 */
export const getFiatCurrencyList = (
  params: getAllCurrencyCodeOperation["parameters"]["query"]
): Promise<getAllCurrencyCodeOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.fiatCurrencyList(), params);
};

/**
 * Переводит договор из ACTIVE в DRAFT (OPERATOR)
 */
export const returnContractToDraft = ({
  id,
}: returnContractToDraftOperation["parameters"]["path"]): Promise<
  returnContractToDraftOperation["responses"]["200"]["content"]
> => {
  return api.post(ENDPOINTS.returnContractToDraft(id));
};

/**
 * Валидация УНК договора (доступно для всех). Проверяет формат УНК и наличие банка в базе данных.
 */

export const getBankByUnk = ({
  unk,
}: validateUnkOperation["parameters"]["query"]): Promise<
  validateUnkOperation["responses"]["200"]["content"]["*/*"]
> => {
  const queryParams = unk ? `?unk=${unk}` : "";
  return api.get(ENDPOINTS.getBankByUnk() + queryParams);
};

/**
 * Получение отчета по заявкам договора.
 */

export const getContractBidsReport = ({
  contractId,
}: generateRequestsByContractReport["parameters"]["path"]): Promise<
  generateRequestsByContractReport["responses"]["200"]["content"]["*/*"]
> => {
  return api.post(ENDPOINTS.contractBidsReport(contractId));
};

/**
 * Вернуть черновик договора для CUSTOMER
 */
export const returnContractToDraftForCustomer = ({
  id,
}: returnContractToDraftForCustomerOperation["parameters"]["path"]): Promise<
  returnContractToDraftForCustomerOperation["responses"]["200"]["content"]
> => {
  return api.post(ENDPOINTS.returnContractToDraftForCustomer(id));
};
